import React, { useEffect, useState } from "react";
import {
  getReferralRequests,
  updateReferralResponse,
  rejectReferralRequest,
} from "../../../ApiServices/ApiServices";
import { handleError, handleSuccess } from "../../../Utils";
import { CircularProgress } from "@mui/material";
import { 
  FiMessageSquare, 
  FiCheckCircle, 
  FiClock, 
  FiMail, 
  FiPhone, 
  FiSend, 
  FiX,
  FiUser,
  FiAlertCircle,
  FiFileText,
  FiExternalLink,
  FiArrowLeft
} from 'react-icons/fi';
import { BsCurrencyRupee } from 'react-icons/bs';
import { getEmail } from "../../../DecodeToken/DecodeToken"; // Add this import

function Referral() {
  const [showPage, setShowPage] = useState("pending");
  const [selectedUser, setSelectedUser] = useState(null);
  const [response, setResponse] = useState("");
  const [referredUsers, setReferredUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [isLeftSectionVisible, setIsLeftSectionVisible] = useState(true);
  const [currentDate, setCurrentDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.title = "Dashboard - Referral Requests";
    fetchReferralRequests();
  }, []);

  // Helper function to calculate time difference
  const getTimeAgo = (messageDate) => {
    const now = new Date();
    const msgDate = new Date(messageDate);
    const diffInMilliseconds = now - msgDate;
    const diffInDays = Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMilliseconds / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));

    if (diffInDays > 0) {
      return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
    } else if (diffInHours > 0) {
      return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
    } else if (diffInMinutes > 0) {
      return diffInMinutes === 1 ? "1 minute ago" : `${diffInMinutes} minutes ago`;
    } else {
      return "Just now";
    }
  };

  // Helper function to format mentor fee
  const getMentorFee = (amount) => {
    if (amount && amount.service_amount) {
      return amount.service_amount === 0 ? "Free" : `${amount.service_amount}`;
    }
    return "Free";
  };

  const fetchReferralRequests = async () => {
    try {
      const mentorEmail = getEmail();
      const data = await getReferralRequests(mentorEmail); 


      if (data.success) {
        const pending = data.referralRequests.filter(
          (req) => req.status === "pending"
        );
        const referred = data.referralRequests.filter(
          (req) => req.status === "referred"
        );
        const rejected = data.referralRequests.filter(
          (req) => req.status === "rejected"
        );

        setPendingUsers(pending);
        setReferredUsers(referred);
        setRejectedUsers(rejected);
        setCurrentDate(data.currentDate);
      } else {
        handleError("Failed to fetch Referral Requests.");
      }
    } catch (error) {
      return handleError(error?.response?.data?.message);
    }
  };

  const handleButtonClick = (page) => {
    setShowPage(page);
    setSelectedUser(null);
    setIsLeftSectionVisible(true);
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setIsLeftSectionVisible(false);
  };

  const handleResponseChange = (event) => {
    setResponse(event.target.value);
  };

  const handleRejectRequest = async () => {
    try {
      await rejectReferralRequest(selectedUser._id);
      setPendingUsers(
        pendingUsers.filter((user) => user._id !== selectedUser._id)
      );
      setRejectedUsers([...rejectedUsers, selectedUser]);
      setSelectedUser(null);
      setIsLeftSectionVisible(true);

      handleSuccess("Referral request rejected successfully");
    } catch (error) {
      return handleError(error?.response?.data?.message);
    }
  };

  const handleSendResponse = async () => {
    if (response.trim()) {
      try {
        setIsLoading(true);
        await updateReferralResponse(selectedUser._id, response);

        setReferredUsers([...referredUsers, { ...selectedUser, reply: response }]);
        setPendingUsers(
          pendingUsers.filter((user) => user._id !== selectedUser._id)
        );
        setResponse("");
        setSelectedUser(null);
        setIsLeftSectionVisible(true);

        handleSuccess("Referral processed successfully");
      } catch (error) {
        return handleError(error?.response?.data?.message);
      } finally {
        setIsLoading(false);
      }
    } else {
      handleError("Please type your response before sending.");
    }
  };

  const handleBackToList = () => {
    setSelectedUser(null);
    setIsLeftSectionVisible(true);
  };

  const getCurrentUsers = () => {
    switch (showPage) {
      case "pending": return pendingUsers;
      case "referred": return referredUsers;
      case "rejected": return rejectedUsers;
      default: return [];
    }
  };

  const getCurrentCount = () => {
    switch (showPage) {
      case "pending": return pendingUsers.length;
      case "referred": return referredUsers.length;
      case "rejected": return rejectedUsers.length;
      default: return 0;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-x-hidden">
      <div className="pt-16 px-4 py-4 max-w-[1600px] mx-auto">
        {/* Header Section */}
        <div className="rounded-2xl p-4 sm:p-6 mb-4 backdrop-blur-sm border bg-white/70 border-gray-200/50 shadow-2xl shadow-blue-500/10 transition-all duration-300">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 sm:p-3 rounded-xl bg-blue-100 text-blue-600">
              <FiFileText size={20} className="sm:w-6 sm:h-6" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-blue-600 bg-clip-text text-transparent">
              Referral Requests
            </h1>
          </div>

          {/* Tab Buttons */}
          <div className="flex gap-2 sm:gap-3">
            <button
              className={`relative px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:scale-105 ${
                showPage === "pending"
                  ? "bg-blue-600 text-white shadow-lg shadow-blue-500/25"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => handleButtonClick("pending")}
            >
              <FiClock className="inline mr-1 sm:mr-2" size={14} />
              Pending
              {pendingUsers.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center font-bold">
                  {pendingUsers.length}
                </span>
              )}
            </button>

            <button
              className={`relative px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:scale-105 ${
                showPage === "referred"
                  ? "bg-green-600 text-white shadow-lg shadow-green-500/25"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => handleButtonClick("referred")}
            >
              <FiCheckCircle className="inline mr-1 sm:mr-2" size={14} />
              Referred
              {referredUsers.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center font-bold">
                  {referredUsers.length}
                </span>
              )}
            </button>

            <button
              className={`relative px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:scale-105 ${
                showPage === "rejected"
                  ? "bg-red-600 text-white shadow-lg shadow-red-500/25"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => handleButtonClick("rejected")}
            >
              <FiX className="inline mr-1 sm:mr-2" size={14} />
              Rejected
              {rejectedUsers.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center font-bold">
                  {rejectedUsers.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4 sm:gap-8">
          {/* Left Section - User List */}
          <div
            className={`${
              isLeftSectionVisible ? "block" : "hidden"
            } xl:block xl:col-span-2 rounded-2xl overflow-hidden backdrop-blur-sm border bg-white/70 border-gray-200/50 shadow-2xl shadow-blue-500/10 transition-all duration-300`}
          >
            <div className="px-4 py-2 border-b border-gray-200/50 bg-gray-50/50">
              <div className="flex items-center gap-3">
                <FiUser className="text-blue-600" size={20} />
                <span className="font-semibold text-gray-800">
                  Requests
                </span>
                <span className="text-sm text-gray-500">
                  ({getCurrentCount()})
                </span>
              </div>
            </div>

            <div className="p-2 max-h-[calc(100vh-280px)] sm:max-h-[600px] overflow-y-auto">
              {/* Empty State */}
              {getCurrentUsers().length === 0 && (
                <div className="text-center py-8 sm:py-12">
                  <div className="p-3 sm:p-4 rounded-full bg-gray-100 w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 flex items-center justify-center">
                    <FiAlertCircle className="text-gray-400" size={20} />
                  </div>
                  <p className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">
                    No {showPage} requests
                  </p>
                  <p className="text-gray-500 mb-4 sm:mb-6 text-sm sm:text-base">
                    {showPage === "pending" 
                      ? "All caught up! No pending requests at the moment." 
                      : `No ${showPage} requests yet.`}
                  </p>
                  <button
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 hover:scale-105 text-sm sm:text-base"
                    onClick={() => setShowPage("pending")}
                  >
                    Go to Pending
                  </button>
                </div>
              )}

              {/* User List */}
              {getCurrentUsers().length > 0 && (
                <div className="space-y-3">
                  {getCurrentUsers().map((user, index) => (
                    <div
                      key={index}
                      className={`relative p-3 sm:p-2 rounded-xl border-2 border-gray-200 bg-white cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                        showPage === "pending" ? "hover:border-blue-300" :
                        showPage === "referred" ? "hover:border-green-300" :
                        "hover:border-red-300"
                      }`}
                      onClick={() => handleUserClick(user)}
                    >
                      <span className="absolute -top-2 left-4 px-2 py-0.5 text-xs font-semibold text-blue-600 border border-blue-100 rounded bg-white">
                        {user.session_title}
                      </span>
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-2 h-2 rounded-full ${
                          showPage === "pending" ? "bg-orange-500" :
                          showPage === "referred" ? "bg-green-500" :
                          "bg-red-500"
                        }`}></div>
                        <span className="font-semibold text-gray-800 text-sm sm:text-base">{user.user_name}</span>
                        <span className="text-xs text-gray-500 ml-auto">
                          {getTimeAgo(user.date)}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-2">
                        {user.message}
                      </p>
                      {showPage === "referred" && user.reply && (
                        <p className="text-xs text-green-600 bg-green-50 p-2 rounded-lg line-clamp-1">
                          Response: {user.reply}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Section - User Details */}
          {selectedUser && (
            <div
              className={`${
                !isLeftSectionVisible ? "block" : "hidden"
              } xl:block xl:col-span-3 rounded-2xl overflow-hidden backdrop-blur-sm border bg-white/70 border-gray-200/50 shadow-2xl shadow-blue-500/10 transition-all duration-300`}
            >
              <div className="px-4 py-2 border-b border-gray-200/50 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Back button for mobile */}
                    <button
                      onClick={handleBackToList}
                      className="xl:hidden p-0 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <FiArrowLeft size={20} className="text-gray-600" />
                    </button>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-blue-600 text-sm sm:text-base">
                        {selectedUser.user_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base truncate">{selectedUser.user_name}</h3>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-500">
                        <div className="flex items-center gap-1 truncate">
                          <FiMail size={10} className="sm:w-3 sm:h-3 flex-shrink-0" />
                          <span className="truncate">{selectedUser.user_email}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <FiPhone size={10} className="sm:w-3 sm:h-3 flex-shrink-0" />
                          <span>{selectedUser.user_phone}</span>
                        </div>
                      </div>
                      {/* Job and Resume info */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-600 mt-1">
                        <div className="flex items-center gap-1 truncate">
                          <FiExternalLink size={10} className="sm:w-3 sm:h-3 flex-shrink-0 text-green-600" />
                          <span className="font-medium">Job:</span>
                          <a 
                            href={selectedUser.jobLink?.startsWith('http') ? selectedUser.jobLink : `https://${selectedUser.jobLink}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline truncate max-w-[100px] sm:max-w-[150px]"
                            title={selectedUser.jobLink}
                          >
                            View Job
                          </a>
                        </div>
                        <div className="flex items-center gap-1">
                          <FiFileText size={10} className="sm:w-3 sm:h-3 flex-shrink-0 text-blue-600" />
                          <span className="font-medium">Resume:</span>
                          <a 
                            href={selectedUser.resume} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                            title="Download Resume"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 sm:gap-3">
                    {showPage === "pending" && (
                      <div className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg bg-orange-100 text-orange-800 text-xs sm:text-sm">
                        <FiClock size={12} className="sm:w-3.5 sm:h-3.5" />
                        <span className="whitespace-nowrap">{getTimeAgo(selectedUser.date)}</span>
                      </div>
                    )}
                    {showPage === "referred" && (
                      <div className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg bg-green-100 text-green-800 text-xs sm:text-sm">
                        <FiCheckCircle size={12} className="sm:w-3.5 sm:h-3.5" />
                        <span>Referred</span>
                      </div>
                    )}
                    {showPage === "rejected" && (
                      <div className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg bg-red-100 text-red-800 text-xs sm:text-sm">
                        <FiX size={12} className="sm:w-3.5 sm:h-3.5" />
                        <span>Rejected</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg bg-blue-100 text-blue-800 text-xs sm:text-sm">
                      <BsCurrencyRupee size={12} className="sm:w-3.5 sm:h-3.5" />
                      <span>{getMentorFee(selectedUser.amount)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 max-h-[calc(100vh-200px)] sm:max-h-none overflow-y-auto">
                {/* Message from User */}
                <div className="mb-6">
                  <div className="flex mb-3">
                    <div className="bg-blue-500 text-white p-3 rounded-2xl rounded-bl-none max-w-[85%] sm:max-w-[80%]">
                      <p className="text-sm">{selectedUser.message}</p>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 ml-1">
                    {getTimeAgo(selectedUser.date)}
                  </div>
                </div>
                
                {/* Response Section */}
                {showPage === "pending" && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Referral Response</h4>
                    <textarea
                      placeholder="Type your referral response here..."
                      value={response}
                      onChange={handleResponseChange}
                      className="w-full p-3 sm:p-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 resize-none text-sm sm:text-base"
                      rows="4"
                    />

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                      {/* <button
                        onClick={handleRejectRequest}
                        className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-xl border-2 border-red-300 text-red-600 hover:bg-red-50 transition-all duration-300 hover:scale-105 text-sm sm:text-base"
                      >
                        <FiX size={16} />
                        Reject Request
                      </button> */}
                      <button
                        onClick={handleSendResponse}
                        disabled={isLoading}
                        className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex-1 text-sm sm:text-base"
                      >
                        {isLoading ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <FiSend size={16} />
                        )}
                        {isLoading ? "Processing..." : "Process Referral"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Referred Response */}
                {showPage === "referred" && (
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Your Response</h4>
                    <div className="flex justify-end mb-3">
                      <div className="bg-green-100 text-gray-800 p-3 rounded-2xl rounded-br-none max-w-[85%] sm:max-w-[80%]">
                        <p className="text-sm">{selectedUser?.reply || "No response available"}</p>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mr-1 text-right">
                      {selectedUser?.repliedOn ? getTimeAgo(selectedUser.repliedOn) : ""}
                    </div>
                  </div>
                )}

                {/* Rejected State */}
                {showPage === "rejected" && (
                  <div className="text-center p-6">
                    <div className="p-4 rounded-full bg-red-100 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <FiX className="text-red-500" size={24} />
                    </div>
                    <p className="text-lg font-semibold text-gray-800 mb-2">Request Rejected</p>
                    <p className="text-gray-600">This referral request has been rejected.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Empty State for Right Panel */}
          {!selectedUser && (
            <div className="hidden xl:flex xl:col-span-3 rounded-2xl backdrop-blur-sm border bg-white/70 border-gray-200/50 shadow-2xl shadow-blue-500/10 items-center justify-center">
              <div className="text-center p-8">
                <div className="p-4 rounded-full bg-gray-100 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <FiFileText className="text-gray-400" size={24} />
                </div>
                <p className="text-xl font-semibold text-gray-600 mb-2">
                  Select a referral request
                </p>
                <p className="text-gray-500">
                  Choose a request from the list to view details and respond
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Referral;